import {Component, OnInit} from '@angular/core';

import {Role, Permission} from 'jugacu-lib';
import {MatTableDataSource} from '@angular/material/table';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogComponent} from '../../../components/confirm-dialog/confirm-dialog.component';
import {take} from 'rxjs/operators';
import {WhistlerAPIService} from '../../../whistler-api.service';

const defaultRole: Role = {
    name: 'new role',
    desc: '',
    color: '',
    badge: '',
    permissions: []
};

@Component({
    selector: 'app-roles',
    templateUrl: './roles.component.html',
    styleUrls: ['./roles.component.scss']
})
export class RolesComponent implements OnInit {
    isLoadingResults = true;
    error = false;

    isEdit: boolean;
    saving: boolean;
    selectedRole: Role;

    dataSource = new MatTableDataSource<Role>();
    displayedColumns = ['Name', 'Desc', 'Badge', 'Color', 'Actions'];

    constructor(
        private readonly whistlerAPIService: WhistlerAPIService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog
    ) {
        this.selectedRole = {...defaultRole};
        this.isEdit = false;
        this.saving = false;
    }

    async ngOnInit() {
        try {
            this.isLoadingResults = true;
            this.dataSource.data = (await this.whistlerAPIService.getRoleList()).data;
        } catch (e) {
            this.error = true;
        } finally {
            this.isLoadingResults = false;
        }
    }

    select(role: Role) {
        this.selectedRole = {...role};
        this.isEdit = true;
    }

    createMode() {
        this.isEdit = false;
        this.selectedRole = {...defaultRole};
    }

    async delete(role: Role, index: number) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                message: `Are you sure want to delete <strong>${role.name}<strong>?`,
                buttonText: {
                    ok: 'Yes',
                    cancel: 'No'
                }
            }
        });

        dialogRef.afterClosed()
            .pipe(
                take(1)
            )
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.confirmDelete(role, index);
                }
            });
    }

    private async confirmDelete(role: Role, index: number) {
        try {
            this.saving = true;

            await this.whistlerAPIService.deleteRole(role);

            const data: Role[] = [
                ...this.dataSource.data
            ];
            data.splice(index, 1);
            this.dataSource.data = data;

            this.snackBar.open('Role deleted.', 'Ok');
        } catch (e) {
            this.snackBar.open('An error has occurred, please try again later.', 'Ok', {duration: 5000});
        } finally {
            this.saving = false;
        }
    }

    async save() {
        try {
            this.saving = true;
            if (this.isEdit) {
                const index = this.dataSource.data.findIndex(role => role.name === this.selectedRole.name);
                const response = await this.whistlerAPIService.saveRole(this.selectedRole);

                const data: Role[] = [
                    ...this.dataSource.data
                ];
                data[index] = response.data;
                this.dataSource.data = data;

                this.snackBar.open('Role saved.', 'Ok');
            } else {
                const response = await this.whistlerAPIService.createRole(this.selectedRole);
                const data: Role[] = [
                    ...this.dataSource.data
                ];
                data.push(response.data);
                this.dataSource.data = data;

                this.snackBar.open('Role created.', 'Ok');
                this.selectedRole = {...defaultRole};
            }

        } catch (e) {
            this.snackBar.open('An error has occurred, please try again later.', 'Ok', {duration: 5000});
        } finally {
            this.saving = false;
        }

    }

    onPermissionRemove(permission: Permission) {
        const index = this.selectedRole.permissions.findIndex(perm => perm.name = permission.name);
        this.selectedRole.permissions.splice(index, 1);
    }

    onPermissionAdd(permission: Permission) {
        const search = this.selectedRole.permissions.find(perm => perm.name === permission.name);
        if (!search) {
            this.selectedRole.permissions.push(permission);
        }
    }

    isUndeleteable(role: Role): boolean {
        const undeleteables = [
            'user', 'banned', 'dev'
        ];
        return !!undeleteables.find(und => und === role.name);
    }
}
